import * as React from 'react';
/* eslint-disable react-refresh/only-export-components */
import {
  useFloating,
  autoUpdate,
  offset,
  autoPlacement,
  flip,
  shift,
  size,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  FloatingFocusManager,
  useMergeRefs,
  type Placement,
} from '@floating-ui/react';

interface PopoverOptions {
  initialOpen?: boolean;
  placement?: Placement;
  modal?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  matchWidth?: boolean;
  /**
   * When true, choose between top/bottom variants of `placement` based on available space.
   * Keeps alignment (`start`/`end`) from the original placement.
   */
  preferMoreSpace?: boolean;
  /** When false, keep the requested placement and constrain the floating content instead. */
  allowFlip?: boolean;
  /** Optional external reference element to anchor to instead of the trigger */
  externalRef?: React.RefObject<HTMLElement | null>;
  /** Optional portal root for popovers that need local stacking behavior. */
  portalRoot?: HTMLElement | null;
}

export function usePopover(options: PopoverOptions = {}) {
  const {
    initialOpen = false,
    placement = 'bottom',
    modal,
    open: controlledOpen,
    onOpenChange: setControlledOpen,
    preferMoreSpace = false,
    allowFlip = true,
    externalRef,
    portalRoot = null,
  } = options;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);
  const [labelId, setLabelId] = React.useState<string | undefined>();
  const [descriptionId, setDescriptionId] = React.useState<string | undefined>();

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const verticalPlacementCandidates = React.useMemo(() => {
    const [side, alignment] = placement.split('-') as [
      'top' | 'bottom' | 'left' | 'right',
      'start' | 'end' | undefined,
    ];

    if (side !== 'top' && side !== 'bottom') {
      return null;
    }

    const format = (nextSide: 'top' | 'bottom') =>
      `${nextSide}${alignment ? `-${alignment}` : ''}` as Placement;

    return [format('bottom'), format('top')];
  }, [placement]);

  const placementMiddleware = !allowFlip
    ? null
    : preferMoreSpace && verticalPlacementCandidates
      ? autoPlacement({
          allowedPlacements: verticalPlacementCandidates,
          padding: 5,
        })
      : flip({
          crossAxis: placement.includes('-'),
          fallbackAxisSideDirection: 'end',
          padding: 5,
        });

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    elements: {
      reference: externalRef?.current,
    },
    middleware: [
      offset(5),
      ...(placementMiddleware ? [placementMiddleware] : []),
      shift({ padding: 5 }),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight - 10}px`,
            ...(options.matchWidth ? { width: `${rects.reference.width}px` } : {}),
          });
        },
      }),
    ],
  });

  const context = data.context;

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const interactions = useInteractions([click, dismiss, role]);

  return React.useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      modal,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
      portalRoot,
    }),
    [
      open,
      setOpen,
      interactions,
      data,
      modal,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
      portalRoot,
    ]
  );
}

type ContextType =
  | (ReturnType<typeof usePopover> & {
      setLabelId: React.Dispatch<React.SetStateAction<string | undefined>>;
      setDescriptionId: React.Dispatch<React.SetStateAction<string | undefined>>;
    })
  | null;

const PopoverContext = React.createContext<ContextType>(null);

export const usePopoverContext = () => {
  const context = React.useContext(PopoverContext);

  if (context == null) {
    throw new Error('Popover components must be wrapped in <Popover />');
  }

  return context;
};

export function Popover({
  children,
  modal = false,
  ...restOptions
}: {
  children: React.ReactNode;
} & PopoverOptions) {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const popover = usePopover({ modal, ...restOptions });
  return <PopoverContext.Provider value={popover}>{children}</PopoverContext.Provider>;
}

interface PopoverTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
}

export const PopoverTrigger = React.forwardRef<
  HTMLElement,
  React.HTMLProps<HTMLElement> & PopoverTriggerProps
>(function PopoverTrigger({ children, asChild = false, ...props }, propRef) {
  const context = usePopoverContext();
  const childrenRef =
    (children as React.ReactElement & { ref?: React.Ref<unknown> }).ref ||
    (React.isValidElement(children) ? (children.props as { ref?: React.Ref<unknown> }).ref : null);

  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(
      children,
      context.getReferenceProps({
        ...props,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...(children.props as any),
        ref,
        'data-state': context.open ? 'open' : 'closed',
      })
    );
  }

  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      type="button"
      data-state={context.open ? 'open' : 'closed'}
      {...context.getReferenceProps(props)}
    >
      {children}
    </button>
  );
});

export const PopoverContent = React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(
  function PopoverContent({ style, ...props }, propRef) {
    const { context: floatingContext, ...context } = usePopoverContext();
    const ref = useMergeRefs([context.refs.setFloating, propRef]);

    if (!floatingContext.open) return null;

    return (
      <FloatingPortal root={context.portalRoot ?? undefined}>
        <FloatingFocusManager context={floatingContext} modal={context.modal} initialFocus={-1}>
          <div
            ref={ref}
            style={{
              display: 'flex',
              flexDirection: 'column',
              zIndex: 'var(--z-menu)',
              ...context.floatingStyles,
              ...style,
            }}
            aria-labelledby={context.labelId}
            aria-describedby={context.descriptionId}
            {...context.getFloatingProps(props)}
          >
            {props.children}
          </div>
        </FloatingFocusManager>
      </FloatingPortal>
    );
  }
);
